// The quiz's per-question screens — dispatched by question type. Every
// screen shares ScreenChrome (components/ScreenChrome) for the page shell —
// it renders its own `<main>`, so nothing here nests a second one — and
// the flow's own pill/card visuals (styles.ts) rather than after-www's.

import { useEffect, useState } from "react";
import { ChevronRightIcon } from "@heroicons/react/24/outline";

import ArcLoading from "components/ArcLoading";
import ChoiceCard from "components/ChoiceCard";
import ChoicePill from "components/ChoicePill";
import ChoiceTile from "components/ChoiceTile";
import { CONTINUE_BUTTON_CLASS } from "components/FormV2";
import ReviewCard from "components/ReviewCard";
import { LINK_BUTTON_CLASS } from "components/FlowButtons";
import type { QuizResponseAnimationVariant } from "pages/QuoteFlow/flags";
import ScreenChrome from "components/ScreenChrome";
import { CARE_TEAM_PHONE_HREF, CARE_TEAM_PHONE_LABEL } from "constants/phones";

import { responseRevealDelayMs } from "./response-delay";
import type { Answers, LabelSegment, Question } from "./types";

// The quiz's choice/pill visuals — the same treatment FormV2's ChoicesField
// and YesNoField give a selectable option, as plain class builders (the
// quiz has no react-hook-form context to hang a Controller off).
/** Back on the left, the forward action on the right — the same footer
 * the application's screens end on. */
const FOOTER_ROW_CLASS =
  "mt-6 flex flex-wrap items-center justify-between gap-4";

/** ScreenChrome's heading takes a plain leading run and a bold, colored
 * trailing run — this port's labels carry emphasis as segments instead.
 * The first emphasized segment onward becomes the trailing run; a label
 * with none puts everything in the leading one. */
function splitForHeading(segments: LabelSegment[]): {
  titleA: string;
  titleB: string;
} {
  const emphasisAt = segments.findIndex((segment) => segment.emphasize);
  if (emphasisAt === -1) {
    return {
      titleA: segments.map((segment) => segment.text).join(""),
      titleB: "",
    };
  }
  return {
    titleA: segments
      .slice(0, emphasisAt)
      .map((segment) => segment.text)
      .join(""),
    titleB: segments
      .slice(emphasisAt)
      .map((segment) => segment.text)
      .join(""),
  };
}

function labelFor(
  question: Question,
  planningFor: "self" | "loved_one",
): LabelSegment[] {
  if (planningFor === "loved_one" && question.labelForLovedOne) {
    return question.labelForLovedOne;
  }
  return question.label ?? [];
}

function BackLink({ onBack }: { onBack?: () => void }) {
  if (!onBack) return null;
  return (
    <button type="button" className={LINK_BUTTON_CLASS} onClick={onBack}>
      Back
    </button>
  );
}

export interface QuizQuestionScreenProps {
  question: Question;
  answers: Answers;
  /** Whether the family's OWN quiz answer to `who` names a loved one —
   * never the server's `planning_for`, which only preselects. */
  addressingLovedOne: boolean;
  preselectedWho?: string;
  responseAnimationVariant: QuizResponseAnimationVariant;
  onAnswer: (value: string | string[]) => void;
  onBack?: () => void;
}

function QuizSingleSelectScreen({
  question,
  answers,
  addressingLovedOne,
  preselectedWho,
  onAnswer,
  onBack,
}: QuizQuestionScreenProps) {
  const saved = answers[question.key];
  const [selected, setSelected] = useState<string>(
    (typeof saved === "string" ? saved : undefined) ??
      (question.key === "who" ? preselectedWho : undefined) ??
      "",
  );
  const { titleA, titleB } = splitForHeading(
    labelFor(question, addressingLovedOne ? "loved_one" : "self"),
  );

  return (
    <ScreenChrome
      kicker="Quick questions"
      titleSize="compact"
      titleA={titleA}
      titleB={titleB}
      blurb=""
    >
      <div className="mt-7 w-full">
        <div className="flex flex-col gap-3">
          {(question.options ?? []).map((option) => (
            <ChoiceCard
              key={option.key}
              selected={selected === option.key}
              onSelect={() => setSelected(option.key)}
              tone="daisy"
              size="lg"
            >
              {option.label}
            </ChoiceCard>
          ))}
        </div>
        <div className={FOOTER_ROW_CLASS}>
          <BackLink onBack={onBack} />
          <button
            type="button"
            className={`${CONTINUE_BUTTON_CLASS} ml-auto`}
            disabled={!selected}
            onClick={() => onAnswer(selected)}
          >
            Next
            <ChevronRightIcon
              className="h-4 w-4"
              strokeWidth={2.5}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </ScreenChrome>
  );
}

function QuizMultiSelectScreen({
  question,
  answers,
  addressingLovedOne,
  onAnswer,
  onBack,
}: QuizQuestionScreenProps) {
  const saved = answers[question.key];
  const [selected, setSelected] = useState<string[]>(
    Array.isArray(saved) ? saved : [],
  );
  const minRequired = question.minRequired ?? 1;
  const { titleA, titleB } = splitForHeading(
    labelFor(question, addressingLovedOne ? "loved_one" : "self"),
  );

  const toggle = (key: string) => {
    setSelected((current) =>
      current.includes(key)
        ? current.filter((k) => k !== key)
        : [...current, key],
    );
  };

  return (
    <ScreenChrome
      kicker="Quick questions"
      titleSize="compact"
      titleA={titleA}
      titleB={titleB}
      blurb=""
    >
      <div className="mt-7 w-full">
        <div className="grid grid-cols-2 gap-3">
          {(question.options ?? []).map((option) => (
            <ChoiceTile
              key={option.key}
              selected={selected.includes(option.key)}
              onSelect={() => toggle(option.key)}
              illustration={option.image}
            >
              {option.label}
            </ChoiceTile>
          ))}
        </div>
        {selected.length < minRequired && (
          <p className="mt-3 text-sm text-oat-700">
            Please select at least {minRequired} option
            {minRequired > 1 ? "s" : ""}
          </p>
        )}
        <div className={FOOTER_ROW_CLASS}>
          <BackLink onBack={onBack} />
          <button
            type="button"
            className={`${CONTINUE_BUTTON_CLASS} ml-auto`}
            disabled={selected.length < minRequired}
            onClick={() => onAnswer(selected)}
          >
            Next
            <ChevronRightIcon
              className="h-4 w-4"
              strokeWidth={2.5}
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </ScreenChrome>
  );
}

function QuizYesNoScreen({
  question,
  answers,
  addressingLovedOne,
  onAnswer,
  onBack,
}: QuizQuestionScreenProps) {
  const saved = answers[question.key];
  const { titleA, titleB } = splitForHeading(
    labelFor(question, addressingLovedOne ? "loved_one" : "self"),
  );

  return (
    <ScreenChrome
      kicker="Quick questions"
      titleSize="compact"
      titleA={titleA}
      titleB={titleB}
      blurb=""
    >
      <div className="mt-7 w-full">
        <div className="flex gap-3" role="group">
          <ChoicePill
            selected={saved === "no"}
            onSelect={() => onAnswer("no")}
            size="lg"
          >
            No
          </ChoicePill>
          <ChoicePill
            selected={saved === "yes"}
            onSelect={() => onAnswer("yes")}
            size="lg"
          >
            Yes
          </ChoicePill>
        </div>
        <div className={FOOTER_ROW_CLASS}>
          <BackLink onBack={onBack} />
          {typeof saved === "string" && (
            <button
              type="button"
              className={`${CONTINUE_BUTTON_CLASS} ml-auto`}
              onClick={() => onAnswer(saved)}
            >
              Next
              <ChevronRightIcon
                className="h-4 w-4"
                strokeWidth={2.5}
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      </div>
    </ScreenChrome>
  );
}

function DeadEndScreen({
  question,
  onBack,
}: {
  question: Question;
  onBack?: () => void;
}) {
  const content = question.content ?? {};
  return (
    <ScreenChrome
      kicker=""
      titleA={content.title ?? ""}
      titleB=""
      blurb=""
      titleSize="compact"
    >
      <div className="mt-7 flex w-full flex-col items-start gap-5">
        <div className="flex flex-col items-start gap-3">
          <a href={CARE_TEAM_PHONE_HREF} className={CONTINUE_BUTTON_CLASS}>
            Call {CARE_TEAM_PHONE_LABEL}
          </a>
          {content.externalLink && (
            <a
              href={content.externalLink}
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-evergreen underline underline-offset-4 hover:text-plum"
            >
              {content.externalLinkText ?? "Continue online"}
            </a>
          )}
        </div>
        <BackLink onBack={onBack} />
      </div>
    </ScreenChrome>
  );
}

function QuizResponseScreen({
  question,
  responseAnimationVariant,
  onAnswer,
  onBack,
}: QuizQuestionScreenProps) {
  // Called unconditionally ahead of the dead-end's early return — the
  // dead end never reads `ready`, but the hook itself must run every
  // render regardless.
  const [ready, setReady] = useState(
    responseRevealDelayMs(responseAnimationVariant) === 0,
  );
  useEffect(() => {
    const delay = responseRevealDelayMs(responseAnimationVariant);
    if (delay === 0) return;
    const timer = setTimeout(() => setReady(true), delay);
    return () => clearTimeout(timer);
    // The variant is fixed once flags commit; re-arming the timer on a
    // later render would restart a hold that already ran.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (question.isDeadEnd)
    return <DeadEndScreen question={question} onBack={onBack} />;

  const content = question.content ?? {};

  return (
    <ScreenChrome
      kicker=""
      titleA={content.title ?? ""}
      titleB=""
      blurb=""
      titleSize="compact"
    >
      <div className="mt-7 flex w-full flex-col items-start gap-5">
        {content.steps && content.steps.length > 0 && (
          <h2 className="text-xl font-semibold text-soil">How it works:</h2>
        )}
        {content.steps && content.steps.length > 0 && (
          <ol className="flex flex-col gap-4">
            {content.steps.map((step, index) => (
              <li key={step} className="flex items-center gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-soil text-xl font-bold text-ivory-light">
                  {index + 1}
                </span>
                <span className="text-lg leading-relaxed text-soil">
                  {step}
                </span>
              </li>
            ))}
          </ol>
        )}
        {content.text &&
          content.text.split("\n\n").map((paragraph) => (
            <p key={paragraph} className="text-xl leading-relaxed text-soil">
              {paragraph}
            </p>
          ))}
        {content.testimonial && (
          <ReviewCard
            quote={content.testimonial.quote}
            attribution={`${content.testimonial.author} · ${content.testimonial.source}`}
          />
        )}
        <div className={`${FOOTER_ROW_CLASS} mt-0 w-full`}>
          <BackLink onBack={onBack} />
          {/* The button is always laid out — the arc sits over its box until
              the reveal, so nothing moves when the button fades in. */}
          <div className="relative ml-auto">
            <button
              type="button"
              className={`${CONTINUE_BUTTON_CLASS} transition-opacity duration-1000 ease-out ${
                ready ? "opacity-100" : "pointer-events-none opacity-0"
              }`}
              aria-hidden={!ready}
              tabIndex={ready ? undefined : -1}
              onClick={() => {
                if (ready) onAnswer("continue");
              }}
            >
              Next
              <ChevronRightIcon
                className="h-4 w-4"
                strokeWidth={2.5}
                aria-hidden="true"
              />
            </button>
            {!ready && (
              <ArcLoading className="absolute inset-0 m-auto h-6 w-[98px] text-soil" />
            )}
          </div>
        </div>
      </div>
    </ScreenChrome>
  );
}

export default function QuizQuestionScreen(props: QuizQuestionScreenProps) {
  if (props.question.type === "response")
    return <QuizResponseScreen {...props} />;
  if (props.question.type === "yesno") return <QuizYesNoScreen {...props} />;
  if (props.question.type === "multiselect")
    return <QuizMultiSelectScreen {...props} />;
  return <QuizSingleSelectScreen {...props} />;
}
